import React, { useRef, useState, useEffect, useLayoutEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import LinkIcon from '@atlaskit/icon/core/link';
import Button from '@atlaskit/button/default/button';
import IconButton from '@atlaskit/button/icon/button';
import type { IconButtonAppearance, Appearance } from '@atlaskit/button/variants/types';
import type { PositionType } from '@atlaskit/tooltip/types';
import type { NewCoreIconProps } from '@atlaskit/icon/types';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';

import { END } from '@confluence/navdex/entry-points/NavdexPointType.ts';

// eslint-disable-next-line import/no-cycle
import { useCopyContentLink } from './useCopyContentLink.ts';
import { useCopyContentLinkActions } from './useCopyContentLinkState.ts';
import { useCopyToClipboard } from './useCopyToClipboard.tsx';

const i18n = defineMessages({
	copyLinkLabel: {
		id: 'copy-content-link.icon.label',
		defaultMessage: 'Copy link',
		description: 'Label and tooltip for the button to copy content URL',
	},
	copiedTextLabel: {
		id: 'copy-content-link.copied.tooltip',
		defaultMessage: 'Copied!',
		description:
			'Text for the copy link button tooltip when content URL has been successfully copied',
	},
	errorCopyingTextLabel: {
		id: 'copy-content-link.error.tooltip',
		defaultMessage: 'Something went wrong.',
		description:
			'Text for the copy link button tooltip when an error occurred while trying to copy link',
	},
});

const DEFAULT_TOOLTIP_DELAY = 100;
const RESET_COPYTEXT_TIMEOUT = 2500;

const iconButtonAppearances = new Set<IconButtonAppearance>([
	'default',
	'primary',
	'discovery',
	'subtle',
]);
const formatIconButtonAppearance = (appearance: Appearance): IconButtonAppearance =>
	iconButtonAppearances.has(appearance as IconButtonAppearance)
		? (appearance as IconButtonAppearance)
		: 'default';

export const ACTION_SUBJECT_ID = 'copyShareLink';

export type PageMode = 'view' | 'edit';

type BaseCopyLinkProps = {
	pageMode?: PageMode;
	contentType: string;
	contentId: string;
	spaceKey: string;
	source: string;
	isDraft?: boolean;
	isLivePage?: boolean;
	isDisabled?: boolean;
	shouldResetOnHover?: boolean;
	buttonAppearance?: Appearance;
	tooltipPosition?: PositionType;
	buttonText?: string | React.ReactElement;
	copiedTextTooltipLabel?: MessageDescriptor;
	onClickAttributes?: { [key: string]: string };
	iconColor?: NewCoreIconProps['color'];
};

// When passing in a urlOverride, all other props are optional and not used to construct the url
type UrlOverrideProps = {
	urlOverride: string;
	source: string;
};

export type CopyLinkProps =
	| (BaseCopyLinkProps & { urlOverride?: never })
	| (UrlOverrideProps & Partial<Omit<BaseCopyLinkProps, 'source'>>);

export const CopyLinkButton: React.FC<CopyLinkProps> = ({
	pageMode,
	contentType,
	contentId,
	spaceKey,
	source,
	isDraft = false,
	isLivePage = false,
	isDisabled = false,
	shouldResetOnHover = false,
	buttonAppearance = 'subtle',
	tooltipPosition = 'bottom',
	buttonText = '',
	copiedTextTooltipLabel = i18n.copiedTextLabel,
	onClickAttributes,
	iconColor,
	urlOverride = '',
}) => {
	const intl = useIntl();
	const cleanupCopiedTimer = useRef<NodeJS.Timeout>();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isCopied, setIsCopied] = useState<boolean>(false);
	const [errorDuringCopy, setErrorDuringCopy] = useState<Error | undefined>();
	const updateTooltip = React.useRef<() => void>();
	// Hook to store copy content link click state
	const { setHasClickedCopyContentLink, resetHasClickedCopyContentLink } =
		useCopyContentLinkActions();
	const { copyToClipboard } = useCopyToClipboard();

	useEffect(() => {
		if (isCopied) {
			cleanupCopiedTimer.current = setTimeout(() => setIsCopied(false), RESET_COPYTEXT_TIMEOUT);
		}
		return () => {
			clearTimeout(cleanupCopiedTimer.current);
		};
	}, [isCopied]);

	useEffect(() => {
		void resetHasClickedCopyContentLink();
	}, [resetHasClickedCopyContentLink]);

	const { unsupportedLinkError, copyContentLink } = useCopyContentLink({
		pageMode,
		contentType,
		contentId,
		spaceKey,
		source,
		skip: Boolean(urlOverride),
	});

	const handleCopyLink = async () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: ACTION_SUBJECT_ID,
				source,
				attributes: {
					pageMode,
					contentType,
					navdexPointType: END,
					isDraft,
					isLivePage,
					...(onClickAttributes ?? {}),
				},
			},
		}).fire();
		//mark has copy content link click to true
		void setHasClickedCopyContentLink();
		try {
			if (!urlOverride) {
				await copyContentLink();
			} else {
				await copyToClipboard(urlOverride);
			}
		} catch (e) {
			setErrorDuringCopy(e);
		}
		setIsCopied(true);
	};

	// report errors from the hook's query silently since we have fallback link retrieval
	// methods, but show user error tooltip text if we actually
	// couldn't get any link to copy or the copy itself failed
	const copyError = unsupportedLinkError || errorDuringCopy;
	const copyLinkText = intl.formatMessage(i18n.copyLinkLabel);
	const confirmationText = intl.formatMessage(
		copyError ? i18n.errorCopyingTextLabel : copiedTextTooltipLabel,
	);

	const onMouseLeave = () => {
		if (shouldResetOnHover) {
			setIsCopied(false);
		}
	};

	useLayoutEffect(() => {
		updateTooltip.current?.();
	}, [isCopied]);

	const iconButtonAppearance = formatIconButtonAppearance(buttonAppearance);

	const getCopyButton = () =>
		buttonText ? (
			<Button
				testId="copy-link-button"
				aria-label={copyLinkText}
				appearance={buttonAppearance}
				iconBefore={LinkIcon}
				onClick={handleCopyLink}
				onMouseLeave={onMouseLeave}
				isDisabled={isDisabled}
			>
				{buttonText}
			</Button>
		) : (
			<IconButton
				testId="copy-link-button"
				label={copyLinkText}
				icon={(iconProps) => <LinkIcon {...iconProps} color={iconColor} />}
				onClick={handleCopyLink}
				onMouseLeave={onMouseLeave}
				isDisabled={isDisabled}
				appearance={iconButtonAppearance}
			/>
		);

	return (
		<>
			{/* Screen reader announcer */}
			<VisuallyHidden role="status" aria-live="assertive" aria-atomic="true">
				{isCopied && confirmationText}
			</VisuallyHidden>
			<Tooltip
				content={({ update }) => {
					updateTooltip.current = update;

					if (isCopied) {
						return confirmationText;
					}

					if (buttonText) {
						return buttonText;
					} else {
						return copyLinkText;
					}
				}}
				position={tooltipPosition}
				ignoreTooltipPointerEvents
				delay={DEFAULT_TOOLTIP_DELAY}
				hasNewContentOnTriggerClick
			>
				{getCopyButton()}
			</Tooltip>
		</>
	);
};
